import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Alert,
  Fade,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CalendarMonth,
  ArrowBack,
  Warning,
  Group,
  CheckCircle,
  ArrowForward,
  Download,
  Timeline,
  MonetizationOn,
  Security,
  ExpandMore,
} from '@mui/icons-material';
import { CTA_LABEL, CTA_SUBLINE, COHORT_START, openCalendly } from '../config';

const encodeForm = (data) =>
  Object.keys(data)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
    .join('&');

const CorporateExitRoadmap = ({ onBack }) => {
  const [activeMonth, setActiveMonth] = useState(1);
  const [responses, setResponses] = useState({});
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleEmailSubmit = async () => {
    if (!email || !email.includes('@')) return;
    setSending(true);
    try {
      // 1) Netlify Form submission (stores lead in dashboard)
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeForm({ 'form-name': 'roadmap-lead', email }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // 2) Resend notification email (fire-and-forget, non-blocking)
      fetch('/api/send-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).catch(() => {});
    } catch (err) {
      try {
        const queued = JSON.parse(localStorage.getItem('ff_pending_leads') || '[]');
        queued.push({ email, at: new Date().toISOString() });
        localStorage.setItem('ff_pending_leads', JSON.stringify(queued));
      } catch (storageErr) {
        console.error('Lead could not be stored:', storageErr);
      }
    } finally {
      setSending(false);
      setEmailSubmitted(true);
    }
  };

  // Rendered on both the email gate and the roadmap itself. Returns null when
  // onBack is absent rather than showing a button that does nothing.
  const BackLink = ({ light = false }) =>
    onBack ? (
      <Button
        onClick={onBack}
        startIcon={<ArrowBack />}
        sx={{
          mb: 2,
          color: light ? 'white' : 'text.secondary',
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        Back to The Freedom Framework
      </Button>
    ) : null;

  const updateResponse = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const months = [
    {
      month: 1,
      title: "Foundation & Reality Check",
      theme: "Assess Your Starting Point",
      keyActions: [
        "Complete financial audit (income, expenses, debts, savings)",
        "Calculate your Financial Freedom Number",
        "Identify your 'Why' - document motivations for leaving",
        "Research 3-5 remote income opportunities aligned with your skills",
        "Begin building emergency fund (target: 6 months expenses)"
      ],
      milestones: "Financial clarity achieved, Freedom Number calculated",
      riskLevel: "Low"
    },
    {
      month: 2,
      title: "Skill Development & Testing",
      theme: "Build Your Remote Income Engine",
      keyActions: [
        "Choose 1-2 remote income streams to develop",
        "Create portfolio or proof of work (website, samples, case studies)",
        "Join 3-5 communities where your ideal clients gather",
        "Land first test client or project (even if low-paid)",
        "Continue saving 30-50% of corporate income"
      ],
      milestones: "First remote income earned, portfolio live",
      riskLevel: "Low"
    },
    {
      month: 3,
      title: "Income Acceleration & Validation",
      theme: "Prove Your Model Works",
      keyActions: [
        "Scale to 3-5 paying clients/customers",
        "Optimize pricing (aim for $50-150/hour equivalent)",
        "Systemize client acquisition (templates, outreach scripts)",
        "Hit $2K/month minimum in side income",
        "Research visa options for 3 target countries"
      ],
      milestones: "$2K+ monthly remote income, repeatable system",
      riskLevel: "Medium"
    },
    {
      month: 4,
      title: "The Crossover Point",
      theme: "When Side Hustle Meets Salary",
      keyActions: [
        "Target $4K-5K/month remote income (50% of typical salary)",
        "Document all systems, processes, templates",
        "Build 3-month financial runway buffer",
        "Have 'soft conversation' with manager about flexibility",
        "Secure 2-3 recurring clients for stability"
      ],
      milestones: "50% income replacement achieved, 3-month runway saved",
      riskLevel: "Medium"
    },
    {
      month: 5,
      title: "The Negotiation & Transition",
      theme: "Design Your Exit Strategy",
      keyActions: [
        "Formal conversation with employer (remote work or departure)",
        "Negotiate notice period, severance, or contractor arrangement",
        "Hit $6K-8K/month remote income target",
        "Finalize country choice and visa application",
        "Book one-way ticket (departure in 30-60 days)"
      ],
      milestones: "Exit date set, income at 75%+ replacement, visa in process",
      riskLevel: "High"
    },
    {
      month: 6,
      title: "Launch Into Freedom",
      theme: "Execute Your Exit",
      keyActions: [
        "Last day at corporate job",
        "Transition all clients to full-time availability",
        "Departure to first location",
        "Set up location-independent systems (banking, mail, etc.)",
        "Join local community, establish routine, celebrate freedom"
      ],
      milestones: "Officially location-independent, income stable at $8K+",
      riskLevel: "Medium (decreasing)"
    }
  ];

  const renderMonthContent = () => {
    const month = months[activeMonth - 1];
    let riskColor = '#4CAF50'; // Low
    let riskBgColor = 'rgba(76, 175, 80, 0.08)';

    if (month.riskLevel.includes('Medium')) {
      riskColor = '#FF9800';
      riskBgColor = 'rgba(255, 152, 0, 0.08)';
    } else if (month.riskLevel.includes('High')) {
      riskColor = '#F44336';
      riskBgColor = 'rgba(244, 67, 54, 0.08)';
    }

    return (
      <Fade in timeout={600}>
        <Box sx={{ mt: 4 }}>
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.05) 0%, rgba(42, 157, 143, 0.05) 100%)',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <CalendarMonth sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                Month {month.month}: {month.title}
              </Typography>
            </Stack>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              {month.theme}
            </Typography>
          </Paper>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper sx={{ p: 4, height: '100%' }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
                  Key Actions This Month
                </Typography>
                <List>
                  {month.keyActions.map((action, idx) => (
                    <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 24, height: 24, fontSize: '0.875rem' }}>
                          {idx + 1}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={action}
                        sx={{ '& .MuiListItemText-primary': { fontSize: '1rem' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: 'success.light',
                    color: 'success.contrastText'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Success Milestones
                  </Typography>
                  <Typography variant="body2">
                    {month.milestones}
                  </Typography>
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: riskBgColor,
                    color: 'text.primary'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Risk Level: {month.riskLevel}
                  </Typography>
                  <Typography variant="body2">
                    {month.riskLevel === 'Low' && "Still employed, building foundation safely"}
                    {month.riskLevel.includes('Medium') && "Transition phase, manageable with planning"}
                    {month.riskLevel.includes('High') && "Critical decision point, ensure runway is solid"}
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
          </Grid>

          <Paper sx={{ p: 4, mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Your Progress Tracker
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder={`Track your Month ${month.month} progress, challenges, and wins here...`}
              value={responses[`month${month.month}_notes`] || ''}
              onChange={(e) => updateResponse(`month${month.month}_notes`, e.target.value)}
              sx={{ '& .MuiInputBase-root': { fontFamily: 'inherit' } }}
            />
          </Paper>
        </Box>
      </Fade>
    );
  };

  // Email capture screen
  if (!emailSubmitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'left' }}>
            <BackLink light />
          </Box>
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background gradient */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
              }}
            />

            <Typography
              variant="h3"
              sx={{
                mb: 3,
                fontWeight: 800,
              }}
            >
              Unlock Your Corporate Exit Roadmap
            </Typography>

            <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
              Join 500+ people who've used this exact 6-month plan to achieve location freedom
            </Typography>

            <Box sx={{ mb: 6 }}>
              <TextField
                fullWidth
                type="email"
                label="Enter your email address"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    py: 1,
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '1.1rem',
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleEmailSubmit}
                disabled={!email || !email.includes('@') || sending}
                sx={{
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {sending ? 'Sending…' : 'Get My Free Roadmap Instantly'}
              </Button>
            </Box>

            {/* Benefits */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[
                '6-month battle-tested timeline',
                'Risk assessment & mitigation strategies',
                'Employer negotiation scripts',
                'Financial runway calculator',
                'Proven with 500+ students'
              ].map((benefit, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CheckCircle sx={{ color: 'success.main' }} />
                    <Typography variant="body1" sx={{ color: 'text.primary' }}>
                      {benefit}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            <Alert severity="info" sx={{ justifyContent: 'center' }}>
              <Typography variant="body2">
                No spam • Unsubscribe anytime • Join our global community of freedom achievers
              </Typography>
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <BackLink />
        {/* Header */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 3 }}>
            <Download sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 800 }}>
                The 6-Month Corporate Exit Roadmap
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mt: 1 }}>
                Your step-by-step blueprint to leave corporate life and achieve location freedom
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={4}>
            <Chip label="Proven with 500+ students" sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }} />
            <Chip label="Built on real exits" sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }} />
            <Chip label="Income before you resign" sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }} />
          </Stack>
        </Paper>

        {/* Timeline Navigation */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: 'primary.main' }}>
            Your 6-Month Journey
          </Typography>

          <Box sx={{ display: 'flex', overflowX: 'auto', pb: 2 }}>
            {months.map((m) => (
              <Button
                key={m.month}
                onClick={() => setActiveMonth(m.month)}
                variant={activeMonth === m.month ? 'contained' : 'outlined'}
                sx={{
                  minWidth: 120,
                  mx: 1,
                  py: 2,
                  flexDirection: 'column',
                  textAlign: 'center',
                  borderRadius: 2,
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Month {m.month}
                </Typography>
                <Typography variant="caption">
                  {m.title.split(' ')[0]} {m.title.split(' ')[1]}
                </Typography>
              </Button>
            ))}
          </Box>
        </Paper>

        {/* Month Content */}
        {renderMonthContent()}

        {/* Financial Runway Calculator */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: 'success.main' }}>
            <MonetizationOn sx={{ mr: 2 }} />
            Financial Runway Calculator
          </Typography>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Monthly Expenses (Current Location)"
                  placeholder="e.g., 4000"
                  value={responses.monthlyExpenses || ''}
                  onChange={(e) => updateResponse('monthlyExpenses', e.target.value)}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Current Savings"
                  placeholder="e.g., 15000"
                  value={responses.savings || ''}
                  onChange={(e) => updateResponse('savings', e.target.value)}
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Current Remote Income (Monthly)"
                  placeholder="e.g., 2000"
                  value={responses.remoteIncome || ''}
                  onChange={(e) => updateResponse('remoteIncome', e.target.value)}
                />
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Your Financial Runway
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2 }}>
                  {responses.monthlyExpenses && responses.savings
                    ? `${(parseFloat(responses.savings) / parseFloat(responses.monthlyExpenses)).toFixed(1)} months`
                    : '-- months'}
                </Typography>

                <Alert
                  severity={
                    responses.monthlyExpenses && responses.savings &&
                    (parseFloat(responses.savings) / parseFloat(responses.monthlyExpenses)) >= 6
                      ? "success"
                      : "warning"
                  }
                  sx={{ justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                >
                  {responses.monthlyExpenses && responses.savings &&
                   (parseFloat(responses.savings) / parseFloat(responses.monthlyExpenses)) >= 6
                    ? 'You have sufficient runway!'
                    : 'Build to 6+ months before exit'}
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Risk Assessment Matrix */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: 'warning.main' }}>
            <Security sx={{ mr: 2 }} />
            Risk Assessment Matrix
          </Typography>

          <Stack spacing={2}>
            {[
              { risk: 'Financial runway less than 6 months', level: 'High', mitigation: 'Delay exit, increase savings rate, reduce expenses' },
              { risk: 'Remote income under $4K/month', level: 'High', mitigation: 'Extend timeline, add more clients, negotiate part-time transition' },
              { risk: 'No recurring clients or revenue', level: 'Medium', mitigation: 'Focus on retainer agreements, productize services, build email list' },
              { risk: 'Visa requirements unclear', level: 'Medium', mitigation: 'Research 2-3 backup countries, consult immigration lawyer, join expat groups' },
              { risk: 'Family or relationship concerns', level: 'Medium', mitigation: 'Have honest conversations, involve partner in planning, start with trial period' },
              { risk: 'Health insurance gaps', level: 'Low', mitigation: 'Research nomad insurance (SafetyWing, Genki), COBRA extension, international plans' }
            ].map((item, idx) => (
              <Accordion key={idx} sx={{ '&::before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: item.level === 'High' ? 'error.light' :
                             item.level === 'Medium' ? 'warning.light' : 'success.light'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
                    <Chip
                      label={item.level}
                      color={
                        item.level === 'High' ? 'error' :
                        item.level === 'Medium' ? 'warning' : 'success'
                      }
                      size="small"
                    />
                    <Typography variant="h6">{item.risk}</Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1">
                    <strong>Mitigation:</strong> {item.mitigation}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Paper>

        {/* Employer Negotiation Scripts */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: 'info.main' }}>
            <Group sx={{ mr: 2 }} />
            Employer Negotiation Scripts
          </Typography>

          <Stack spacing={4}>
            <Paper sx={{ p: 3, borderLeft: 4, borderColor: 'info.main' }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'info.main' }}>
                Script 1: Testing the Waters (Month 3-4)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                "I wanted to touch base about my role here. I've been thinking about ways to optimize my productivity and work-life balance. Have you seen success with any team members working remotely or with flexible arrangements? I'm curious about what options might be available as I plan for the coming year."
              </Alert>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'info.main' }}>
                Goal: Gauge openness without commitment
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, borderLeft: 4, borderColor: 'success.main' }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'success.main' }}>
                Script 2: The Remote Work Proposal (Month 4-5)
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                "I'd like to discuss a remote work arrangement. I've been consistently exceeding my targets, and I've researched how remote work could actually increase my productivity while reducing costs for the company. I'd propose a 3-month trial where we set clear KPIs. I'm prepared to be available during core hours and maintain all deliverables. Can we explore this together?"
              </Alert>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'success.main' }}>
                Goal: Present as business benefit, not personal request
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, borderLeft: 4, borderColor: 'secondary.main' }}>
              <Typography variant="h5" sx={{ mb: 2, color: 'secondary.main' }}>
                Script 3: The Graceful Exit (Month 5)
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                "I want to be transparent with you. I've decided to pursue location-independent work and will be transitioning out of my role. I'm giving [X weeks] notice and want to ensure a smooth handover. I'm happy to document all processes, train my replacement, and remain available for questions during the transition. I've valued my time here and want to leave on excellent terms. What timeline works best for you?"
              </Alert>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'secondary.main' }}>
                Goal: Professional, generous, maintain relationship
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: 'warning.light' }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'warning.dark', fontWeight: 600 }}>
                Negotiation Tips
              </Typography>
              <List dense>
                {[
                  'Always frame as business benefit, not personal preference',
                  'Have data ready: your performance metrics, cost savings, productivity research',
                  'Propose trial periods to reduce perceived risk',
                  'Be prepared to walk away if remote work isn\'t approved',
                  'Document everything in writing for contractor agreements',
                  'Never burn bridges - you never know when paths will cross again'
                ].map((tip, idx) => (
                  <ListItem key={idx} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        •
                      </Typography>
                    </ListItemIcon>
                    <ListItemText primary={tip} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Stack>
        </Paper>

        {/* CTA Section */}
        <Paper
          sx={{
            p: 6,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 800 }}>
            Ready for the Complete System?
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            This roadmap gives you the foundation, but The Freedom Framework provides everything you need to execute flawlessly:
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircle />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    6 Live Coaching Modules
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Weekly guidance + accountability
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircle />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    50+ Country Database
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Visa pathways, costs, communities
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircle />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    500+ Member Community
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Support from those who've done it
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <CheckCircle />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    $8K-15K Income Templates
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Proven remote income systems
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            size="large"
            sx={{
              py: 2,
              px: 6,
              fontSize: '1.25rem',
              fontWeight: 700,
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
            onClick={openCalendly}
          >
            {CTA_LABEL}
            <ArrowForward sx={{ ml: 2 }} />
          </Button>

          <Typography variant="body2" sx={{ mt: 2, opacity: 0.9, maxWidth: 620, mx: 'auto' }}>
            {CTA_SUBLINE}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, maxWidth: 620, mx: 'auto' }}>
            We take the roadmap you just read and rebuild Months 1 to 6 around your salary, your
            savings and your visa options. The {COHORT_START} cohort takes 10 people.
          </Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This roadmap is an excerpt from Module 5 of The Freedom Framework
          </Typography>
          <Typography variant="caption">
            Proven with 500+ students • Used across 20+ countries
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CorporateExitRoadmap;
